import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import type { SvgIconComponent } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material';

interface SelectableCardProps {
  label: string;
  description?: string;
  selected: boolean | undefined;
  onClick: () => void;
  icon: SvgIconComponent;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  sx?: SxProps<Theme>;
}

export function SelectableCard({
  label,
  description,
  selected,
  onClick,
  icon: Icon,
  color = 'primary',
  sx,
}: SelectableCardProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        p: 3,
        borderRadius: 3,
        cursor: 'pointer',
        transition: 'all 0.25s cubic-bezier(.4,0,.2,1)',
        border: 2,
        borderColor: selected ? `${color}.main` : 'divider',
        bgcolor: selected ? `${color}.50` : 'transparent',
        '&:hover': {
          borderColor: selected ? `${color}.main` : 'action.disabled',
          bgcolor: selected ? `${color}.50` : 'action.hover',
        },
        // Em dark mode, o .50 do MUI não existe — fallback manual
        ...(selected && {
          bgcolor: (theme: Theme) =>
            theme.palette.mode === 'dark'
              ? `rgba(${theme.palette[color].main}, 0.08)`
              : `${color}.50`,
        }),
        ...sx,
      }}
    >
      {/* Check */}
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 24,
          height: 24,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.25s ease',
          bgcolor: selected ? `${color}.main` : 'action.disabledBackground',
          transform: selected ? 'scale(1)' : 'scale(0.85)',
        }}
      >
        {selected && <CheckIcon sx={{ fontSize: 16, color: `${color}.contrastText` }} />}
      </Box>

      <Icon
        sx={{
          fontSize: 36,
          color: selected ? `${color}.main` : 'action.disabled',
          transition: 'color 0.25s ease',
        }}
      />

      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: selected ? 'text.primary' : 'text.disabled',
            transition: 'color 0.25s ease',
          }}
        >
          {label}
        </Typography>
        {description && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.25, display: 'block' }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}